/**
 * CakePackagesCard — Cake Vendor dashboard widget.
 *
 * Each package = ONE image + name + dual price matrix (Egg / Eggless),
 * rows = flavour, cols = weight (kg). Vendor leaves a cell blank if
 * (flavour, weight) is not offered.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Cake, Plus, FloppyDisk, Trash, UploadSimple, X,
  Spinner, Pencil,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_URL}${u}`;
};

const blankPkg = (defaults) => ({
  name: '',
  description: '',
  image_url: '',
  flavours: [...defaults.flavours],
  weights_kg: [...defaults.weights],
  prices_egg: {},
  prices_eggless: {},
  min_lead_time_hours: 24,
  delivery_included: true,
});

const fmtWeight = (w) => (Number.isInteger(w) ? String(w) : String(w));

// ---------------------------------------------------------------------------
// Matrix editor — kg as columns, flavour as rows
// ---------------------------------------------------------------------------
const PriceMatrix = ({ title, badge, flavours, weights, prices, onChange, testIdPrefix }) => {
  const cellValue = (flavour, weight) => {
    const wKey = fmtWeight(weight);
    return prices?.[flavour]?.[wKey] ?? '';
  };
  const setCell = (flavour, weight, raw) => {
    const wKey = fmtWeight(weight);
    const next = { ...(prices || {}) };
    const row = { ...(next[flavour] || {}) };
    const num = parseInt(raw, 10);
    if (!raw || isNaN(num) || num <= 0) {
      delete row[wKey];
    } else {
      row[wKey] = num;
    }
    if (Object.keys(row).length === 0) {
      delete next[flavour];
    } else {
      next[flavour] = row;
    }
    onChange(next);
  };

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold">{title}</span>
        <span className={`text-[10px] px-1.5 py-0.5 ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
        <span className="text-[11px] text-text-secondary">(leave blank if not offered)</span>
      </div>
      <div className="overflow-x-auto border border-[#E8E1D5]">
        <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
          <thead className="bg-sand-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-sand-50 border-r border-[#E8E1D5]">
                Flavour
              </th>
              {weights.map((w) => (
                <th key={w} className="px-2 py-2 font-semibold whitespace-nowrap">
                  {fmtWeight(w)} kg
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {flavours.map((fl) => (
              <tr key={fl} className="border-t border-[#E8E1D5]">
                <td className="px-3 py-1 sticky left-0 bg-white border-r border-[#E8E1D5] whitespace-nowrap">
                  {fl}
                </td>
                {weights.map((w) => (
                  <td key={w} className="px-1 py-1">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      placeholder="₹"
                      className="h-8 w-20 rounded-none border-[#E8E1D5] text-xs px-1.5"
                      value={cellValue(fl, w)}
                      onChange={(e) => setCell(fl, w, e.target.value)}
                      data-testid={`${testIdPrefix}-cell-${fl.toLowerCase().replace(/\s+/g, '_')}-${fmtWeight(w)}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-package editor
// ---------------------------------------------------------------------------
const PackageEditor = ({ initial, defaults, onSave, onCancel, saving }) => {
  const [pkg, setPkg] = useState(() => initial || blankPkg(defaults));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const update = (patch) => setPkg((cur) => ({ ...cur, ...patch }));

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files (JPG, PNG, WEBP) accepted');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large — max 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update({ image_url: data.url });
      toast.success('Cake photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const submit = () => {
    if (!pkg.name?.trim() || pkg.name.trim().length < 2) {
      toast.error('Give the cake a name (e.g. "Pinata Cake")');
      return;
    }
    if (!pkg.image_url) {
      toast.error('Upload a cake photo first');
      return;
    }
    onSave(pkg);
  };

  return (
    <div className="p-4 border border-[#E8E1D5] bg-sand-50 space-y-3"
      data-testid="cake-package-editor">
      {/* Image */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:w-48 h-32 bg-white border border-[#E8E1D5] grid place-items-center overflow-hidden">
          {pkg.image_url ? (
            <img src={resolveImg(pkg.image_url)} alt={pkg.name}
              className="w-full h-full object-cover" />
          ) : (
            <Cake size={42} className="text-stone-300" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <Input value={pkg.name}
            placeholder="Cake name (e.g. Pinata Cake, Photo Cake)"
            className="rounded-none border-[#E8E1D5]"
            onChange={(e) => update({ name: e.target.value })}
            data-testid="cake-pkg-name" />
          <Textarea value={pkg.description || ''}
            placeholder="Short description (optional)"
            rows={2}
            className="rounded-none border-[#E8E1D5] resize-none"
            onChange={(e) => update({ description: e.target.value })}
            data-testid="cake-pkg-desc" />
          <input ref={fileRef} type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])} />
          <Button variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-none border-[#E8E1D5]"
            data-testid="cake-pkg-upload">
            {uploading ? <Spinner size={14} className="mr-2 animate-spin" /> : <UploadSimple size={14} className="mr-2" />}
            {pkg.image_url ? 'Replace photo' : 'Upload cake photo'}
          </Button>
        </div>
      </div>

      {/* Egg matrix */}
      <PriceMatrix title="Egg cake prices (₹)"
        badge={{ label: 'WITH EGG', bg: 'bg-amber-100', text: 'text-amber-800' }}
        flavours={pkg.flavours || defaults.flavours}
        weights={pkg.weights_kg || defaults.weights}
        prices={pkg.prices_egg}
        onChange={(m) => update({ prices_egg: m })}
        testIdPrefix="cake-egg" />

      {/* Eggless matrix */}
      <PriceMatrix title="Eggless cake prices (₹)"
        badge={{ label: 'EGGLESS', bg: 'bg-green-100', text: 'text-green-800' }}
        flavours={pkg.flavours || defaults.flavours}
        weights={pkg.weights_kg || defaults.weights}
        prices={pkg.prices_eggless}
        onChange={(m) => update({ prices_eggless: m })}
        testIdPrefix="cake-eggless" />

      {/* Lead time + delivery */}
      <div className="grid grid-cols-2 gap-3 pt-2">
        <div>
          <label className="block text-xs font-semibold mb-1">Min lead time (hours)</label>
          <Input type="number" min="1" max="168"
            value={pkg.min_lead_time_hours}
            onChange={(e) => update({ min_lead_time_hours: parseInt(e.target.value, 10) || 24 })}
            className="rounded-none border-[#E8E1D5] h-9"
            data-testid="cake-pkg-lead-time" />
        </div>
        <label className="flex items-end gap-2 pb-1.5 cursor-pointer">
          <input type="checkbox" checked={pkg.delivery_included}
            onChange={(e) => update({ delivery_included: e.target.checked })}
            className="h-4 w-4"
            data-testid="cake-pkg-delivery" />
          <span className="text-sm">Delivery included in price</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-[#E8E1D5]">
        <Button variant="outline" onClick={onCancel}
          className="rounded-none border-[#E8E1D5]"
          data-testid="cake-pkg-cancel">
          <X size={14} className="mr-1" /> Cancel
        </Button>
        <Button onClick={submit} disabled={saving}
          className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
          data-testid="cake-pkg-save">
          {saving ? <Spinner size={14} className="mr-1 animate-spin" /> : <FloppyDisk size={14} className="mr-1" />}
          Save cake
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------
const CakePackagesCard = () => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | <pkg> | null
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/me/cake-packages`,
        { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
      setData({ packages: [], cap: 10, used: 0,
        default_flavours: [], default_weights_kg: [] });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <Card className="bg-white border-[#E8E1D5]">
        <CardContent className="py-6 text-sm text-text-secondary text-center">
          Loading cakes…
        </CardContent>
      </Card>
    );
  }

  const defaults = {
    flavours: data.default_flavours || [],
    weights: data.default_weights_kg || [],
  };

  const savePackage = async (pkg) => {
    setSaving(true);
    try {
      if (pkg.id) {
        await axios.put(`${BACKEND_URL}/api/me/cake-packages/${pkg.id}`, pkg,
          { withCredentials: true });
        toast.success('Cake updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/me/cake-packages`, pkg,
          { withCredentials: true });
        toast.success('Cake added');
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const removePackage = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/me/cake-packages/${pkg.id}`,
        { withCredentials: true });
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="cake-packages-card">
      <CardHeader>
        <CardTitle className="font-heading text-2xl flex items-center gap-2">
          <Cake size={22} className="text-brand-primary" weight="fill" /> My Cake Catalogue
        </CardTitle>
        <CardDescription>
          Upload up to {data.cap} cakes. For each cake, set Egg and Eggless prices
          per flavour × weight in the tables. Leave a cell blank if you don't
          offer that combination.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Existing packages */}
        {data.packages.length === 0 && editing === null && (
          <p className="text-sm text-text-secondary text-center py-6">
            No cakes added yet. Click "Add a cake" to get started.
          </p>
        )}
        <div className="space-y-3">
          {data.packages.map((p) => (
            <div key={p.id}
              className="flex flex-col sm:flex-row gap-3 p-3 border border-[#E8E1D5]"
              data-testid={`cake-row-${p.id}`}>
              <div className="w-full sm:w-32 h-24 bg-stone-200 overflow-hidden shrink-0">
                {p.image_url
                  ? <img src={resolveImg(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center text-stone-400"><Cake size={32} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{p.name}</div>
                {p.description && (
                  <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">{p.description}</div>
                )}
                <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-text-secondary">
                  <span>{Object.keys(p.prices_egg || {}).length} egg flavour{Object.keys(p.prices_egg || {}).length === 1 ? '' : 's'}</span>
                  <span>·</span>
                  <span>{Object.keys(p.prices_eggless || {}).length} eggless flavour{Object.keys(p.prices_eggless || {}).length === 1 ? '' : 's'}</span>
                  <span>·</span>
                  <span>{p.min_lead_time_hours}h lead time</span>
                  {p.delivery_included && <><span>·</span><span>Delivery included</span></>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditing(p)}
                  className="rounded-none border-[#E8E1D5] h-9"
                  data-testid={`cake-edit-${p.id}`}>
                  <Pencil size={12} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" onClick={() => removePackage(p)}
                  className="rounded-none border-[#E8E1D5] text-red-600 hover:bg-red-50 h-9"
                  data-testid={`cake-delete-${p.id}`}>
                  <Trash size={12} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Editor */}
        {editing && (
          <div className="mt-4">
            <PackageEditor
              initial={editing === 'new' ? null : editing}
              defaults={defaults}
              onSave={savePackage}
              onCancel={() => setEditing(null)}
              saving={saving}
            />
          </div>
        )}

        {/* Add button */}
        {!editing && data.used < data.cap && (
          <Button onClick={() => setEditing('new')}
            className="mt-4 rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
            data-testid="cake-add-btn">
            <Plus size={14} className="mr-1" /> Add a cake ({data.used}/{data.cap})
          </Button>
        )}
        {!editing && data.used >= data.cap && (
          <p className="mt-3 text-sm text-amber-700">
            Cake cap reached ({data.used}/{data.cap}). Delete an existing cake to add a new one.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CakePackagesCard;
